import { action, computed, observable } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import type {
  InventoryItemMutator,
  InventoryItemOperator,
} from '@/features/legends/effects/effects'
import {
  MutatorEditorViewModelBase,
  type MutatorEditorViewModelOptions,
} from '@/features/legends/effects/MutatorEditorViewModelBase'
import type { EntityDescriptor } from '@/features/legends/editor/entities'
import { Types } from '@/features/legends/editor/ryno-types'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'
import { when } from '@/utils/validx-util'

export interface InventoryItemMutatorProperties {
  quantity: boolean
  courtesyMultiplier: boolean
}

/**
 * Which of the service item's properties the mutator changes, as picked in
 * the editor.
 */
export type InventoryItemPropertiesChoice =
  'Quantity' | 'CourtesyMultiplier' | 'Both'

const propertiesForChoice: Record<
  InventoryItemPropertiesChoice,
  InventoryItemMutatorProperties
> = {
  Both: { quantity: true, courtesyMultiplier: true },
  CourtesyMultiplier: { quantity: false, courtesyMultiplier: true },
  Quantity: { quantity: true, courtesyMultiplier: false },
}

/**
 * Changes the quantity or courtesy multiplier of service item elements, by a
 * number or a formula.
 */
export class InventoryItemMutatorEditorViewModel extends MutatorEditorViewModelBase<InventoryItemMutator> {
  private readonly _schema: IValidationSchema<InventoryItemMutatorEditorViewModel> =
    {
      ...this.targetsSchema,
      expr: [
        when(
          () => this.mode === 'Expr',
          func(() => this.expr.analyze(), 'There are formula errors.'),
        ),
      ],
    }

  readonly type = 'InventoryItem'

  /**
   * The amount, when it is a formula.
   */
  readonly expr: RynoEditorViewModel

  @observable accessor mode: 'Expr' | 'Value' = 'Value'
  @observable accessor op: InventoryItemOperator = 'Inc'
  @observable accessor quantity: number | null = null
  @observable accessor properties: InventoryItemMutatorProperties = {
    quantity: true,
    courtesyMultiplier: false,
  }

  readonly validation = validationContext(this, this._schema)

  constructor(options: MutatorEditorViewModelOptions) {
    super(
      options,
      'Please select the service item element(s) you wish to change.',
    )
    this.expr = new RynoEditorViewModel({
      required: () => this.mode === 'Expr',
      getSymbolTable: () => this.symbolTable,
      getNeededType: () => Types.number,
    })
    this.lifecycle.manage(this.expr)
  }

  /**
   * Formulas can use `Value`, the item's current quantity.
   */
  @computed
  get symbolTable() {
    return this._symbolTableWithValue(Types.number)
  }

  /**
   * Whether the amount is a formula rather than a number.
   */
  @computed
  get isExprMode() {
    return this.mode === 'Expr'
  }

  /**
   * The changed properties, as picked in the editor.
   */
  @computed
  get propertiesChoice(): InventoryItemPropertiesChoice {
    if (this.properties.quantity && this.properties.courtesyMultiplier) {
      return 'Both'
    }
    return this.properties.courtesyMultiplier
      ? 'CourtesyMultiplier'
      : 'Quantity'
  }

  /**
   * Sets how the amount changes the properties.
   */
  @action.bound
  setOperator(op: InventoryItemOperator) {
    this.op = op
  }

  /**
   * Sets the amount, when it is a number.
   */
  @action.bound
  setQuantity(quantity: number | null) {
    this.quantity = quantity
  }

  /**
   * Switches the amount between a number and a formula.
   */
  @action.bound
  toggleMode() {
    this.mode = this.mode === 'Expr' ? 'Value' : 'Expr'
  }

  /**
   * Sets the properties the mutator changes.
   */
  @action.bound
  setPropertiesChoice(choice: InventoryItemPropertiesChoice) {
    this.properties = { ...propertiesForChoice[choice] }
  }

  /** @inheritDoc */
  @action
  hydrate(mutator: InventoryItemMutator) {
    super.hydrate(mutator)
    if (mutator.op) {
      this.op = mutator.op
    }
    if (typeof mutator.quantity === 'string') {
      this.expr.setValue(mutator.quantity)
      this.mode = 'Expr'
    } else {
      this.quantity = mutator.quantity ?? null
      this.mode = 'Value'
    }
    const { properties } = mutator
    if (properties) {
      this.properties = {
        quantity: !!properties.quantity,
        courtesyMultiplier: !!properties.courtesyMultiplier,
      }
    }
  }

  /** @inheritDoc */
  toJSON(): InventoryItemMutator {
    // No scenario wants neither property, so that falls back to quantity.
    const properties =
      !this.properties.quantity && !this.properties.courtesyMultiplier
        ? { quantity: true, courtesyMultiplier: false }
        : { ...this.properties }
    return {
      type: this.type,
      targets: this.targets.toJSON(),
      op: this.op,
      properties,
      quantity:
        this.mode === 'Expr' ? this.expr.toJSON() : (this.quantity ?? 0),
    }
  }

  /** @inheritDoc */
  protected _isCompatibleTarget({ type }: EntityDescriptor) {
    return type === 'InventoryItem'
  }
}
